/**
 * The single English message catalog. English is the only shipped language in
 * v1; the structure exists so a language switcher can be added without
 * touching components. The `cards` namespace is owned by the card generator
 * feature (`./cards.ts`) and merged in `../index.ts`.
 */
export const en = {
  app: {
    name: 'Open Songster',
  },
  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    retry: 'Try again',
    loading: 'Loading…',
    save: 'Save',
    delete: 'Delete',
    remove: 'Remove',
    refresh: 'Refresh',
    year: 'Year',
    years: 'Years',
    tracks: 'Tracks',
    track: 'Track',
    dismiss: 'Dismiss',
    from: 'From',
    to: 'To',
    of: 'of',
  },
  nav: {
    home: 'Play',
    playlists: 'Playlists',
    settings: 'Settings',
    skipToContent: 'Skip to content',
    primary: 'Main navigation',
    goBack: 'Go back',
  },
  setup: {
    title: 'This instance is not configured yet',
    body: 'The following build-time settings are missing, so sign-in and playlist storage will not work:',
    hint: 'Run the OpenTofu stack, then rebuild the web app with the generated .env.production. See infra/README.md.',
  },
  auth: {
    signInTitle: 'Sign in to save playlists',
    signInBody:
      'Your playlists live in your own account, so you can curate on a laptop and play from your phone.',
    signInWithGoogle: 'Sign in with Google',
    signOut: 'Sign out',
    signedInAs: 'Signed in as {name}',
    callbackWorking: 'Finishing sign-in…',
    callbackFailed: 'Sign-in failed',
    stateMismatch: 'The sign-in response did not match this browser session. Please try again.',
    sessionExpired: 'Your session expired. Please sign in again.',
    required: 'Sign in to continue.',
  },
  spotify: {
    title: 'Spotify',
    connect: 'Connect Spotify',
    connected: 'Connected',
    disconnect: 'Disconnect Spotify',
    connectBody:
      'Open Songster reads your playlists and plays music through your own Spotify account. Your Spotify tokens never leave this device.',
    premiumRequiredTitle: 'Spotify Premium required',
    premiumRequiredBody:
      'Playback needs a Premium account. Spotify removed track previews for new apps, and the embedded player would show the artist and title on screen — which would give the game away. There is no free-account mode.',
    notConnected: 'Spotify is not connected.',
    reconnect: 'Reconnect Spotify',
    tokenExpired: 'The Spotify session expired. Please reconnect.',
    callbackWorking: 'Connecting Spotify…',
    callbackFailed: 'Could not connect Spotify',
    scopeHint: 'Requested access: read your playlists, and control playback.',
    connectShort: 'Connect Spotify to play music from your own account.',
  },
  devices: {
    title: 'Playback device',
    refresh: 'Refresh devices',
    none: 'No Spotify devices found. Open the Spotify app on this phone, then refresh.',
    thisBrowser: 'This browser',
    active: 'Active',
    nativeRecommended: 'Recommended — survives screen lock',
    browserFallbackHint:
      'In-browser playback can be suspended when the screen locks. The Spotify app on your phone is the safer choice mid-party.',
    select: 'Use this device',
    automatic: 'Automatic',
    automaticHint:
      'Plays on whatever device is selected in the Spotify app — including speakers not listed here.',
    speakerHint:
      'Speakers such as Sonos only show up here after you start playing something on them from the Spotify app once. Start a song there, then refresh.',
    useThisBrowser: 'Play in this browser',
    transferFailed: 'Could not switch to that device.',
  },
  home: {
    yourPlaylists: 'Your playlists',
    managePlaylists: 'Manage playlists',
    addFirst: 'Add a playlist',
    noneReady: 'None of your playlists are playable yet. Open one to see what is missing.',
  },
  playlists: {
    title: 'Playlists',
    empty: 'No playlists yet.',
    emptyHint: 'Paste a Spotify playlist link below to get started.',
    addTitle: 'Add a Spotify playlist',
    addPlaceholder: 'https://open.spotify.com/playlist/…',
    add: 'Add playlist',
    adding: 'Reading playlist from Spotify…',
    invalidLink: 'That does not look like a Spotify playlist link.',
    invalidTrackLink: 'That does not look like a Spotify track link.',
    ownershipError:
      'You can only add playlists you own or collaborate on. This is a Spotify restriction for development-mode apps, not a bug.',
    ownershipHint:
      'To use someone else\u2019s playlist: open it in Spotify, select all tracks, and add them to a new playlist of your own \u2014 then paste that link here instead. A playlist its owner has marked collaborative also works if you have been added to it.',
    notFound: 'Spotify could not find that playlist.',
    empty_playlist: 'That playlist has no playable tracks.',
    duplicate: 'You already saved that playlist. Open it and use Refresh instead.',
    noSource: 'This playlist was built by hand, so there is nothing to refresh from.',
    filterTitle: 'Filter by year range',
    filterHint: 'Show playlists that have qualifying years inside this range.',
    filterReset: 'Reset filter',
    filterActive: 'Filtered to {range}',
    noneMatchFilter: 'No playlists have qualifying years in {range}.',
    trackCount: '{count} track | {count} tracks',
    deleteConfirmTitle: 'Delete this playlist?',
    deleteConfirmBody: 'It is removed from your account. The Spotify playlist itself is untouched.',
  },
  playlist: {
    title: 'Playlist',
    valid: 'Ready to play',
    invalid: 'Not playable yet',
    validHint:
      '{count} qualifying year — enough tracks for a game. | {count} qualifying years — enough tracks for a game.',
    invalidHint:
      'A year needs at least 5 tracks to be playable. This playlist has no such year yet.',
    resolving: 'Resolving release years…',
    resolvingHint: 'Release years are looked up in the background. You can leave this screen.',
    pending: 'Queued for year resolution',
    failed: 'Year resolution failed',
    failedHint: 'You can retry by refreshing the playlist.',
    resolved: '{resolved} of {total} track resolved | {resolved} of {total} tracks resolved',
    unresolved:
      '{count} track could not be dated and will not be used. | {count} tracks could not be dated and will not be used.',
    tracksPerYearTitle: 'Songs per year',
    playableYearsShort: '{count} year | {count} years',
    tracksPerYearHint:
      'Each playable year contributes {count} song, and {years} years are in play. | Each playable year contributes {count} songs, and {years} years are in play.',
    tracksPerYearDeckHint:
      'Remove any {remove} card of each year from your deck — it does not matter which. | Remove any {remove} cards of each year from your deck — it does not matter which.',
    breakdownTitle: 'Tracks per year',
    breakdownHint: 'Years with 5 or more tracks are playable. Others go unused.',
    qualifying: 'Playable',
    notQualifying: 'Too few tracks',
    tracksTitle: 'Tracks',
    sourceMusicbrainz: 'MusicBrainz',
    sourceSpotify: 'Spotify release',
    sourceSpotifyFallback: 'Unverified',
    sourceNone: 'Unresolved',
    sourceManual: 'Set by you',
    sourceHint:
      'The logo shows where each year came from. Spotify’s own release date is used for ordinary albums; MusicBrainz is consulted for live records and compilations. An amber ring means MusicBrainz had no match, so the date is unverified — those are worth a look.',
    unevenTitle: 'Uneven years allowed',
    unevenHint:
      'Play every year that has at least one song, instead of only years with a full set. Cards a year cannot fill are skipped during the game.',
    unevenEffect:
      'Turning this on takes the game from {strict} years to {uneven}, at the cost of {skips} cards you will skip.',
    unevenNoEffect: 'Every year in this playlist already has a full set, so this changes nothing.',
    yearEditHint:
      'Tap a year to correct it. Your year is kept through refreshes; clear the field to go back to the resolved one.',
    yearEditLabel: 'Release year for {title}',
    refreshing: 'Re-reading the playlist from Spotify…',
    yearsTitle: 'Years in play',
    noYears: 'No year has enough tracks yet.',
    play: 'Start a game',
    openInSpotify: 'Open in Spotify',
    candidatesTooltip: 'Spotify {spotify} · MusicBrainz {musicbrainz}',
  },
  game: {
    startTitle: 'Start a game',
    startBody: 'Pick a playlist. Your printed cards decide which years you need.',
    chooseAnother: 'Choose another playlist',
    setupTitle: 'Exclude years',
    headerTitle: 'Gameplay',
    excludedTitle: 'Set these cards aside',
    excludedBody:
      'This playlist cannot cover every year of the deck. Take the cards for these years out, then confirm to begin.',
    excludedRangesTitle: 'Excluded year ranges',
    excludedWarning:
      'Warning: a set-aside card still scans. Open Songster will tell you it is not part of this game rather than play a song.',
    unevenTitle: 'Expect to skip some cards',
    unevenBody: '{skips} card has no song behind it. | {skips} cards have no song behind them.',
    unevenLateHint:
      'A card only takes a song when you scan it, so the empty ones mostly turn up late in the game rather than interrupting the start. When one comes up you will see the usual “not part of this game” message — just put it aside and draw again.',
    unevenThinTitle: 'Years with fewer songs than cards',
    excludedNone: 'Every year of the deck is covered — play with the whole deck.',
    includedTitle: 'Years in play',
    confirmStart: 'Confirm & start game',
    preparing: 'Shuffling cards…',
    activeTitle: 'Game in progress',
    playlistLabel: 'Playing from {name}',
    scan: 'Scan card',
    scanNext: 'Scan next card',
    scanToStart: 'Scan a card to start the round',
    scanning: 'Point the camera at a card',
    stopScanning: 'Stop scanning',
    cameraDenied:
      'Camera access was denied. Allow camera access in your browser settings to scan cards.',
    cameraUnavailable: 'No camera is available on this device.',
    scannerUnsupported: 'This browser cannot scan QR codes.',
    nowPlaying: 'Now playing',
    guessTheTrack: 'Guess the track',
    maskedTrack: '??? — ???',
    checkCardForYear: 'Check the physical card for the year',
    hiddenSong: 'Song hidden',
    hiddenSongHint: 'Guess the year, then flip the card.',
    reveal: 'Reveal',
    revealed: 'Revealed',
    hideAgain: 'Hide',
    play: 'Play',
    pause: 'Pause',
    stop: 'Stop',
    noActiveDevice:
      'Spotify has no active device. Start a song on the device you want in the Spotify app, then scan again.',
    deviceUnavailable: 'The selected playback device is gone. Pick another one in Settings.',
    playbackFailed: 'Playback failed. Check that Spotify is open on the selected device.',
    notInGame: 'That card is not part of this game.',
    notInGameHint: 'Set this card aside and scan the next one.',
    unknownCode: 'That is not an Open Songster card.',
    endGame: 'End game',
    endConfirmTitle: 'End this game?',
    endConfirmBody: 'The card assignment is discarded. You cannot get this exact game back.',
    newGame: 'New game',
    alreadyRunningTitle: 'A game is already running',
    alreadyRunningBody: 'End it before starting a new one — only one game can be active at a time.',
    resume: 'Resume game',
    restoredNotice: 'Your running game was restored.',
    playlistGone: 'The playlist for the running game is no longer available.',
  },
  desktop: {
    addTrack: {
      title: 'Add a track',
      body: 'Paste a Spotify track link. The song is added to the end of this playlist and its release year is looked up.',
      placeholder: 'https://open.spotify.com/track/…',
      add: 'Add track',
      added: 'Track added.',
      alreadyPresent: 'That track is already in this playlist.',
    },
    findReplace: {
      title: 'Find & replace',
      body: 'Rewrite many titles at once — strip a “(Remastered)” or a “- 2012 Remaster” from a whole album in one go. Nothing changes until you apply; each result is kept as your title for that track.',
      find: 'Find',
      replace: 'Replace with',
      regex: 'Regular expression',
      caseSensitive: 'Match case',
      invalidPattern: 'That is not a valid regular expression.',
      noMatches: 'No title matches.',
      previewCount: '{count} title will change | {count} titles will change',
      more: '…and {count} more | …and {count} more',
      apply: 'Apply to {count} title | Apply to {count} titles',
    },
    tagline: 'Studio',
    library: 'Library',
    searchPlaylists: 'Filter playlists',
    emptyLibrary: 'No playlists yet. Import one from Spotify to get started.',
    noneSelected: 'Pick a playlist on the left, or start a new one.',
    newPlaylist: 'New playlist',
    create: 'Create',
    nameLabel: 'Playlist name',
    rename: 'Rename playlist',
    importFromSpotify: 'Import from Spotify',
    createFromLink: 'Import a Spotify playlist',
    createFromLinkBody:
      'Paste a Spotify playlist link. It becomes a new playlist here, and the release years are looked up in the background.',
    mergeTitle: 'Import from Spotify',
    mergeIntoCurrent: 'Add tracks to “{name}”',
    mergeBody:
      'Paste a Spotify playlist link. Its tracks are added to this playlist. Tracks you already have keep their years and titles; nothing is removed.',
    merge: 'Add tracks',
    importResult:
      '{added} track added, {duplicates} already in the playlist. | {added} tracks added, {duplicates} already in the playlist.',
    refreshFromSource: 'Pull new tracks',
    refreshFromSourceHint:
      'Re-read the Spotify playlist this was imported from and add anything new.',
    welcomeTitle: 'Build the deck your table will play',
    welcomeBody:
      'Import a Spotify playlist or start from scratch, settle release years where the sources disagree, and tidy titles before they are read out at the reveal.',
    signInBody:
      'Sign in with the Google account you use on your phone. The playlists you edit here are the ones the mobile app plays.',
    emptyPlaylist: 'This playlist has no tracks yet. Import a Spotify playlist to fill it.',
    searchTracks: 'Search tracks',
    noMatches: 'No tracks match “{query}”.',
    sortBy: 'Sort by',
    sort: {
      position: 'Playlist order',
      year: 'Release year',
      artist: 'Artist',
      title: 'Title',
      album: 'Album',
      genre: 'Genre',
    },
    columns: {
      title: 'Track',
      album: 'Album',
      genre: 'Genre',
      year: 'Year',
      actions: 'Actions',
    },
    editTitle: 'Edit title',
    resetTitle: 'Restore Spotify’s title',
    originalTitle: 'Spotify: {title}',
    editHint:
      'Click a title to rename it. Click a year to see what each source says, pick one, type one, or look the track up again.',
    removeTrack: 'Remove from playlist',
    removeTrackConfirmTitle: 'Remove “{title}”?',
    removeTrackConfirmBody: 'Only this playlist changes. The Spotify playlist is untouched.',
    year: {
      title: 'Release year',
      candidates: 'What the sources say',
      spotify: 'Spotify',
      musicbrainz: 'MusicBrainz',
      notAsked: 'not consulted',
      nothingFound: 'no match',
      use: 'Use',
      manual: 'Set a year',
      resolve: 'Look up the missing source',
      pickHint:
        'Use a source’s year, or type your own. Either choice survives imports; a typed year shows as set by you.',
    },
    switchToMobile: 'Mobile app',
    switchToMobileHint: 'Scanning cards and playing music happen in the mobile app.',
  },
  settings: {
    language: 'Language',
    languageHint: 'Follows your device language unless you pick one here.',
    switchToDesktop: 'Switch to the desktop version',
    switchToDesktopHint:
      'The desktop version is for building and editing playlists on a big screen; it cannot scan cards or play music.',
  },
  errors: {
    generic: 'Something went wrong.',
    network: 'The network request failed. Check your connection.',
    unauthorized: 'You are not signed in.',
    notFound: 'Not found.',
    badRequest: 'That request was not understood.',
    alreadySaved: 'That playlist is already saved. Refresh it instead of adding it again.',
    changedElsewhere: 'The playlist changed somewhere else. Reload and try again.',
    server: 'The server had a problem. Please try again.',
    spotifyRateLimited: 'Spotify is rate-limiting us. Wait a moment and try again.',
  },
} as const

/** Default export too, so every locale module has the same shape. */
export default en
